import { useNode } from "@craftjs/core";
import React from "react";
import { isUpdatedFromDefault } from "../../utils";

import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RichTextFormatting,
} from "../../input";
import { supportLibrary } from "../../block-supports";

export const TabSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    tab = ["Tab 1", "Tab 2", "Tab 3"],
    initialOpen,
    minHeight,
    maxWidth,
    showTabSubtitle,
    align,
    tabsGap,
    tabContentGap,
    typoSize,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    titleTextColor,
    titleBackgroundColor,
    titlePadding = { top: "10px", botton: "20px", left: "10px", right: "20px" },
    titleMargin,
    contentBackgroundColor,
    contentPadding,
    contentMargin,
    contentBorder,
    titleBorderWidth,
    titleBorderStyle,
    titleBorderRadius,
    titleBorderColor,
    contentBorderWidth,
    contentBorderStyle,
    contentBorderRadius,
    contentBorderColor,
    tabPosition,
  } = props;

  function GeneralSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing="1rem">
            <SelectInput
              resetValue={undefined}
              value={initialOpen}
              onChange={(val) => {
                setProp((props) => {
                  props.initialOpen = val;
                });
              }}
              options={tab.map((item, i) => {
                return { label: item, value: i };
              })}
              label="Initial Open"
            />
            <SelectInput
              resetValue={undefined}
              value={tabPosition}
              onChange={(val) => {
                setProp((props) => {
                  props.tabPosition = val;
                });
              }}
              options={[
                { label: "Top", value: "top" },
                { label: "Bottom", value: "bottom" },
                { label: "Left", value: "left" },
                { label: "Right", value: "right" },
              ]}
              label="Tab Position"
            />
            <UnitInput
              resetValue={undefined}
              value={minHeight}
              onChange={(val) => {
                setProp((props) => {
                  props.minHeight = val;
                });
              }}
              label={"Min Height"}
            />
            <UnitInput
              resetValue={undefined}
              value={maxWidth}
              onChange={(val) => {
                setProp((props) => {
                  props.maxWidth = val;
                });
              }}
              label={"Max Width"}
            />
            <ToggleInput
              resetValue={undefined}
              label="Show Tab Subtitle"
              value={showTabSubtitle}
              onChange={(val) => {
                setProp((props) => {
                  props.showTabSubtitle = val;
                });
              }}
            />
            <AlignInput
              resetValue={undefined}
              value={align}
              onChange={(val) => {
                setProp((props) => {
                  props.align = val;
                });
              }}
              label={"Align"}
            />
          </Spacer>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: tabsGap },
              { key: tabContentGap },
            ])}
            id={`block-setting-${id}-container-gap`}
            title={"Container Gap"}
            initialOpen={true}
          >
            <UnitInput
              resetValue={undefined}
              value={tabsGap}
              onChange={(val) => {
                setProp((props) => {
                  props.tabsGap = val;
                });
              }}
              label={"Tabs Gap"}
            />
            <UnitInput
              resetValue={undefined}
              value={tabContentGap}
              onChange={(val) => {
                setProp((props) => {
                  props.tabContentGap = val;
                });
              }}
              label={"Tabs Content Gap"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: typoSize },
              { key: typoLineHeight },
              { key: typoLetterSpacing },
              { key: typoWordSpacing },
              { key: typoWeight },
              { key: typoTransform },
              { key: typoDecoration },
              { key: typoStyle },
              {
                key: titlePadding,
                default: {
                  top: "10px",
                  botton: "20px",
                  left: "10px",
                  right: "20px",
                },
              },
              { key: titleMargin },
              { key: titleTextColor },
              { key: titleBackgroundColor },
              { key: titleBorderColor },
            ])}
            id={`block-setting-${id}-title`}
            title={"Title"}
            initialOpen={false}
          >
            <UnitInput
              resetValue={undefined}
              value={typoSize}
              onChange={(val) => {
                setProp((props) => {
                  props.typoSize = val;
                });
              }}
              label={"Size"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoLineHeight}
              onChange={(val) => {
                setProp((props) => {
                  props.typoLineHeight = val;
                });
              }}
              label={"Line Height"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoLetterSpacing}
              onChange={(val) => {
                setProp((props) => {
                  props.typoLetterSpacing = val;
                });
              }}
              label={"Letter Spacing"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoWordSpacing}
              onChange={(val) => {
                setProp((props) => {
                  props.typoWordSpacing = val;
                });
              }}
              label={"Word Spacing"}
            />
            <SelectInput
              resetValue={undefined}
              value={typoWeight}
              onChange={(val) => {
                setProp((props) => {
                  props.typoWeight = val;
                });
              }}
              options={[
                { label: "100", value: "100" },
                { label: "200", value: "200" },
                { label: "300", value: "300" },
                { label: "400", value: "400" },
                { label: "500", value: "500" },
                { label: "600", value: "600" },
                { label: "700", value: "700" },
                { label: "800", value: "800" },
                { label: "900", value: "900" },
              ]}
              label="Weight"
            />
            <SelectInput
              resetValue={undefined}
              value={typoTransform}
              onChange={(val) => {
                setProp((props) => {
                  props.typoTransform = val;
                });
              }}
              options={[
                { label: "Uppercase", value: "uppercase" },
                { label: "Lowercase", value: "lowercase" },
                { label: "Capitalize", value: "capitalize" },
                { label: "None", value: "none" },
              ]}
              label="Transform"
            />
            <SelectInput
              resetValue={undefined}
              value={typoDecoration}
              onChange={(val) => {
                setProp((props) => {
                  props.typoDecoration = val;
                });
              }}
              options={[
                { label: "Underline", value: "underline" },
                { label: "Overline", value: "overline" },
                { label: "None", value: "none" },
              ]}
              label="Decoration"
            />
            <SelectInput
              resetValue={undefined}
              value={typoStyle}
              onChange={(val) => {
                setProp((props) => {
                  props.typoStyle = val;
                });
              }}
              options={[
                {
                  label: "Normal",
                  value: "normal",
                },
                {
                  label: "Italic",
                  value: "italic",
                },
              ]}
              label="Style"
            />
          </Panel>
          <RichTextFormatting />
        </div>
      </>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: titleTextColor },
              { key: titleBackgroundColor },
              { key: titleBorderColor },
            ])}
            id={`block-setting-${id}-title`}
            title={"Title"}
            initialOpen={false}
          >
            <ColorInput
              resetValue={undefined}
              value={titleTextColor}
              onChange={(val) => {
                setProp((props) => {
                  props.titleTextColor = val;
                });
              }}
              label={"Text"}
            />
            <ColorInput
              resetValue={undefined}
              value={titleBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
            <ColorInput
              resetValue={undefined}
              value={titleBorderColor}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBorderColor = val;
                });
              }}
              label={"Title Border"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([{ key: contentBackgroundColor }])}
            id={`block-setting-${id}-content`}
            title={"Content"}
            initialOpen={false}
          >
            <ColorInput
              resetValue={undefined}
              value={contentBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.contentBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: titleMargin },
              { key: titlePadding },
            ])}
            id={`block-setting-${id}-title`}
            title={"Title Layout"}
            initialOpen={false}
          >
            <MarginInput
              resetValue={undefined}
              label={"Margin"}
              value={titleMargin}
              onChange={(val) => {
                setProp((props) => {
                  props.titleMargin = val;
                });
              }}
            />
            <PaddingInput
              resetValue={undefined}
              label={"Padding"}
              value={titlePadding}
              onChange={(val) => {
                setProp((props) => {
                  props.titlePadding = val;
                });
              }}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: contentPadding },
              { key: contentMargin },
            ])}
            id={`block-setting-${id}-content`}
            title={"Content Layout"}
            initialOpen={false}
          >
            <MarginInput
              resetValue={undefined}
              label={"Margin"}
              value={contentMargin}
              onChange={(val) => {
                setProp((props) => {
                  props.contentMargin = val;
                });
              }}
            />
            <PaddingInput
              resetValue={undefined}
              label={"Padding"}
              value={contentPadding}
              onChange={(val) => {
                setProp((props) => {
                  props.contentPadding = val;
                });
              }}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: titleBorderWidth },
              { key: titleBorderStyle },
              { key: titleBorderRadius },
              { key: titleBorderColor },
            ])}
            id={`block-setting-${id}-title-border`}
            title={"Title Border"}
            initialOpen={true}
          >
            <UnitInput
              resetValue={undefined}
              value={titleBorderWidth}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBorderWidth = val;
                });
              }}
              label={"Border Width"}
            />
            <SelectInput
              resetValue={undefined}
              value={titleBorderStyle}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBorderStyle = val;
                });
              }}
              options={[
                { label: "None", value: "none" },
                { label: "Hidden", value: "hidden" },
                { label: "Dotted", value: "dotted" },
                { label: "Dashed", value: "dashed" },
                { label: "Solid", value: "solid" },
                { label: "Double", value: "double" },
                { label: "Groove", value: "groove" },
                { label: "Ridge", value: "ridge" },
                { label: "Inset", value: "inset" },
                { label: "Outset", value: "outset" },
              ]}
              label="Border Style"
            />
            <BorderRadiusInput
              resetValue={undefined}
              value={titleBorderRadius}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBorderRadius = val;
                });
              }}
              label={"Border Radius"}
            />
            <ColorInput
              resetValue={undefined}
              value={titleBorderColor}
              onChange={(val) => {
                setProp((props) => {
                  props.titleBorderColor = val;
                });
              }}
              label={"Border Color"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([{ key: contentBorder }])}
            id={`block-setting-${id}-border`}
            title={"Content Border"}
            initialOpen={false}
          >
            <BorderInput
              resetValue={undefined}
              value={contentBorder}
              onChange={(val) => {
                setProp((props) => {
                  props.contentBorder = val;
                });
              }}
              label={"Border"}
            />
          </Panel>

          {supports &&
            supports.map((support) => {
              var Component = supportLibrary[support]?.component;

              if (!Component) {
                return;
              }

              return (
                <>
                  <Component
                    block={{
                      id,
                      props,
                      dom,
                    }}
                    actions={{
                      setProp,
                    }}
                  ></Component>
                </>
              );
            })}
        </div>
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: initialOpen },
        { key: minHeight },
        { key: maxWidth },
        { key: showTabSubtitle },
        { key: align },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: tabsGap },
        { key: tabContentGap },
        { key: titleBorderColor },
        { key: typoSize },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
        {
          key: titlePadding,
          default: { top: "10px", botton: "20px", left: "10px", right: "20px" },
        },
        { key: titleMargin },
        { key: titleTextColor },
        { key: titleBackgroundColor },
        { key: titleBorderColor },
        { key: contentPadding },
        { key: contentMargin },
        { key: contentBackgroundColor },
        { key: contentBorder },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: titleBorderWidth },
        { key: titleBorderStyle },
        { key: titleBorderRadius },
        { key: titleBorderColor },
        { key: contentBorderWidth },
        { key: contentBorderStyle },
        { key: contentBorderRadius },
        { key: contentBorderColor },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
